import { Injectable } from '@angular/core';
import { LogError, Metadata, RunView } from '@memberjunction/core';
import { GraphQLActionClient, GraphQLDataProvider } from '@memberjunction/graphql-dataprovider';
import { OutboundActionOutcome } from './outbound-action.client';

// ─────────────────────────────────────────────────────────────────────────────
// Local type projections for the 'Outbound: Launch Brand Pipeline' action.
//
// Mirror the server-side LaunchBrandPipelineAction outputs (Plan /
// CreatedSequences / PersonaSequenceMap / WasDryRun) as read-only shapes so the
// UI package has NO dependency on the server-side Marketing package. Everything
// coming back from RunAction is validated with hand-rolled guards (RunAction's
// ResultData is a JSON string, not typed Params).
// ─────────────────────────────────────────────────────────────────────────────

/** One persona in the launch brand pack the modal collects. */
export interface LaunchPersona {
    name: string;
    criteria: string;
    apolloListName: string;
    peopleListName?: string;
    excludeLabels?: string[];
}

/** One sequence spec — a persona + its (provided or generated) copy doc. */
export interface LaunchSequenceSpec {
    personaName: string;
    copyDocument: string;
}

/** One authorized-user grant. */
export interface LaunchAuthorizedUser {
    userEmail: string;
    role: 'Admin' | 'Operator' | 'Viewer';
}

/** The full brand pack sent to the launch action (mirrors BrandPackSchema). */
export interface LaunchBrandPack {
    companyId: string;
    brandSlug?: string;
    emailAccountIds: number[];
    defaultScheduleId: number;
    authorizedUsers: LaunchAuthorizedUser[];
    personas: LaunchPersona[];
    sequences: LaunchSequenceSpec[];
}

/** One step in the returned launch Plan. */
export interface LaunchPlanStep {
    order: number;
    step: string;
    status: 'ok' | 'skipped' | 'planned' | 'error';
    detail: string;
}

/** One created sequence (live runs only). */
export interface LaunchCreatedSequence {
    personaName: string;
    sequenceId: number;
    status: string;
}

/** Combined result of the launch action. */
export interface LaunchResult {
    plan: LaunchPlanStep[];
    createdSequences: LaunchCreatedSequence[];
    personaSequenceMap: Record<string, number>;
    wasDryRun: boolean;
    message: string;
}

// ─── guards (RunAction.Result is a JSON string → validate before use) ───────

function isRecord(v: unknown): v is Record<string, unknown> {
    return typeof v === 'object' && v !== null && !Array.isArray(v);
}
function strOr(v: unknown, fallback: string): string {
    return typeof v === 'string' ? v : fallback;
}
function numOr0(v: unknown): number {
    return typeof v === 'number' && Number.isFinite(v) ? v : 0;
}

function toPlan(v: unknown): LaunchPlanStep[] {
    if (!Array.isArray(v)) return [];
    return v.filter(isRecord).map((s) => {
        const status = strOr(s['status'], 'ok');
        const valid: LaunchPlanStep['status'] =
            status === 'ok' || status === 'skipped' || status === 'planned' || status === 'error' ? status : 'ok';
        return {
            order: numOr0(s['order']),
            step: strOr(s['step'], ''),
            status: valid,
            detail: strOr(s['detail'], ''),
        };
    });
}

function toCreatedSequences(v: unknown): LaunchCreatedSequence[] {
    if (!Array.isArray(v)) return [];
    return v.filter(isRecord).map((s) => ({
        personaName: strOr(s['personaName'], ''),
        sequenceId: numOr0(s['sequenceId']),
        status: strOr(s['status'], 'unknown'),
    }));
}

function toPersonaSequenceMap(v: unknown): Record<string, number> {
    const map: Record<string, number> = {};
    if (!isRecord(v)) return map;
    for (const [k, val] of Object.entries(v)) {
        if (typeof val === 'number' && Number.isFinite(val)) map[k] = val;
    }
    return map;
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Output param Name → Value extracted from RunAction's ResultData. */
function extractOutputs(result: unknown): Map<string, unknown> {
    const outputs = new Map<string, unknown>();
    const entries: unknown[] = Array.isArray(result) ? result : isRecord(result) ? Object.values(result) : [];
    for (const p of entries) {
        if (isRecord(p) && typeof p['Name'] === 'string') outputs.set(p['Name'], p['Value']);
    }
    return outputs;
}

/**
 * Typed client for the brand-launch spine. Invokes the 'Outbound: Launch Brand
 * Pipeline' action by registered name via the framework's GraphQLActionClient.
 * The name carries the 'Outbound: ' prefix, so it is already in the dashboard's
 * action-id map.
 *
 * PROVIDE-ONLY: there is NO generate-copy helper and NO start/unpause affordance
 * — the only knob is the DryRun flag the modal toggles; DryRun=true plans with
 * zero writes.
 */
@Injectable()
export class LaunchBrandPipelineClient {
    private readonly actionIdCache = new Map<string, string>();
    private actionIdMapLoaded = false;

    /**
     * Launch (or DryRun-plan) a brand. PROVIDE-ONLY / no-send: this launcher
     * never passes `People`, so the spine only stages sequences PAUSED-ready — a
     * human enrolls + flips them live later via the Reply dashboard / Enroll UI.
     */
    async launch(
        brandPack: LaunchBrandPack,
        dryRun: boolean,
    ): Promise<OutboundActionOutcome<LaunchResult>> {
        if (!brandPack.companyId || !UUID_RE.test(brandPack.companyId)) {
            return { kind: 'error', message: 'BrandPack.companyId must be a valid UUID' };
        }
        const inputs: { Name: string; Value: string }[] = [
            { Name: 'BrandPack', Value: JSON.stringify(brandPack) },
            { Name: 'DryRun', Value: String(dryRun) },
        ];
        const call = await this.runAction('Outbound: Launch Brand Pipeline', inputs);
        if (!call.found) return { kind: 'not-deployed', message: call.message };
        if (!call.success) return { kind: 'error', message: call.message };
        return {
            kind: 'success',
            data: {
                plan: toPlan(call.outputs.get('Plan')),
                createdSequences: toCreatedSequences(call.outputs.get('CreatedSequences')),
                personaSequenceMap: toPersonaSequenceMap(call.outputs.get('PersonaSequenceMap')),
                wasDryRun: call.outputs.get('WasDryRun') === true,
                message: call.message,
            },
        };
    }

    // ─── transport (mirrors GraphQLOutboundActionClient) ────────────────────

    private async ensureActionIdMap(): Promise<void> {
        if (this.actionIdMapLoaded) return;
        const rv = new RunView();
        const result = await rv.RunView<{ ID: string; Name: string }>({
            EntityName: 'MJ: Actions',
            ExtraFilter: `Name LIKE 'Outbound: %'`,
            Fields: ['ID', 'Name'],
            ResultType: 'simple',
        });
        if (!result.Success) {
            throw new Error(result.ErrorMessage || 'Failed to load Outbound action metadata');
        }
        for (const row of result.Results ?? []) this.actionIdCache.set(row.Name, row.ID);
        this.actionIdMapLoaded = true;
    }

    private async runAction(
        actionName: string,
        params: { Name: string; Value: string }[],
    ): Promise<{ found: boolean; success: boolean; message: string; outputs: Map<string, unknown> }> {
        try {
            await this.ensureActionIdMap();
            const actionId = this.actionIdCache.get(actionName);
            if (!actionId) {
                return {
                    found: false,
                    success: false,
                    message: `Action '${actionName}' is not registered in this environment's metadata`,
                    outputs: new Map(),
                };
            }
            const client = new GraphQLActionClient(Metadata.Provider as GraphQLDataProvider);
            const result = await client.RunAction(
                actionId,
                params.map((p) => ({ Name: p.Name, Value: p.Value, Type: 'Input' as const })),
            );
            const success = result.Success === true;
            return {
                found: true,
                success,
                message: result.Message || (success ? 'OK' : 'Unknown error'),
                outputs: extractOutputs(result.Result),
            };
        } catch (err: unknown) {
            const msg = err instanceof Error ? err.message : String(err);
            LogError(`[LaunchBrandPipelineClient] '${actionName}' failed: ${msg}`);
            return { found: true, success: false, message: msg, outputs: new Map() };
        }
    }
}
